import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import MessageBox from 'yoda-core-components/lib/components/MessageBox/MessageBox';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { DP_USER_STATE } from 'yoda-site-components/lib/common/Constants';
import * as siteAccountAction from 'yoda-site-components/lib/actions/AccountAction';
import MissingPointsModal from 'yoda-account-components/lib/components/MissingPointsModal/MissingPointsConnect';
import {
    getRewardsHistory,
    getActivityHistory,
    getMissingPointsHistory,
    getTransactionDetails,
    getRewardsHistoryPL,
    getActivityHistoryPL,
    setDefaultTabHistory,
    missingPointsLoadAction,
    transactionDetailsModal,
    getHistoryFilterData,
} from '../../actions/HistoryAction';
import { missingPointModalEvent } from '../../actions/RewardsAction';
import {
    getAppliedCashpass,
    applyCashpass,
    removeCashpass,
    clearCashpassError,
} from '../../actions/CashpassCartAction';

import RewardsBgPattern from '../../assets/images/rewards-bg-pattern.png';
import ResponsiveTabNav from '../ResponsiveTabNav/ResponsiveTabNav';
import NewHistoryData from './components/NewHistoryData';
import NewActivityHistoryData from './components/NewActivityHistoryData';
import NewMissingPointsDetails from './components/NewMissingPointsDetails';
import ActivityHistoryDetails from '../History/ActivityHistoryDetails/ActivityHistoryDetails';
import MissingPointsBanner from '../MissingPointsBanner/MissingPointsBanner';
import HistoryCard from '../HistoryCard/HistoryCard';
import AvailableRewards from '../History/AvailableRewards/AvailableRewards';

const LIMIT = 10;

const CASHPASS_ERROR_FALLBACK =
    'Reward could not be applied. The rewards service is temporarily unavailable. Please try again later.';

// Informational (not a hard error) — an empty cart restricts rewards. Its
// MessageBox is rendered as "information" rather than "error".
const EMPTY_CART_RESTRICT_REWARDS = 'SRV_EMPTY_CART_RESTRICT_REWARDS';

const TABS = [
    { label: 'Available Rewards', value: 'available' },
    { label: 'Rewards History', value: 'rewards' },
    { label: 'Activity History', value: 'activity' },
    { label: 'Missing Points History', value: 'missing' },
];

const RewardsHistory = () => {
    const dispatch = useDispatch();

    const historyDetails = useSelector((s) => s.historyDetails?.rewardsData || {});
    const activityHistoryDetails = useSelector((s) => s.historyDetails?.activityData || {});
    const missingPointsHistory = useSelector((s) => s.historyDetails?.missingPointsHistory || {});
    const transactionDetails = useSelector((s) => s.historyDetails?.transactionDetailsData || {});
    const showLoader = useSelector((s) => s.historyDetails?.showLoader || false);
    const deviceType = useSelector((s) => s.context?.deviceType || {});
    const messagesTexts = useSelector((s) => s.context?.messagesTexts || {});
    const preferences = useSelector((s) => s.context?.preferences || {});
    const featureFlags = useSelector((s) => s.context?.featureFlags || {});
    const isNative = useSelector((s) => s.context?.isNative || false);
    const availableCertificates = useSelector(
        (s) => s.rewardsDetails?.details?.data?.certificates?.data || []
    );
    const appliedCashpassMap = useSelector((s) => s.cashpassCart?.bySerialNumber || {});
    const cashpassLoading = useSelector((s) => s.cashpassCart?.loading || false);
    const cashpassError = useSelector((s) => s.cashpassCart?.errorMessage || null);
    const cashpassErrorCode = useSelector((s) => s.cashpassCart?.errorCode || null);
    const cashpassHasError = useSelector((s) => Boolean(s.cashpassCart?.errorSerialNumber));

    /* ── Same logic as RewardsWelcomeBannerAndPoints.showMissing ── */
    const handleAddMissingPoints = (e) => {
        e.preventDefault();
        const userState = TokenProvider.get(DP_USER_STATE);
        const deviceTypeStr = deviceType.isMobile ? 'mobile' : 'desktop';
        const routeTo = ((preferences || {}).navigateToSignIn || {})[deviceTypeStr] || '';
        if (userState === '2' && featureFlags?.enableSignInSlider) {
            dispatch(
                siteAccountAction.openSignInSlider(
                    false,
                    preferences.navigateToRewardsDashboardWithMissingPoints
                )
            );
        } else if (userState === '1') {
            setShowMissingPoints(true);
            dispatch(missingPointModalEvent());
        } else {
            window.location.assign(
                `${routeTo}?next=${preferences.navigateToRewardsDashboardWithMissingPoints}`
            );
        }
    };

    const [showMissingPoints, setShowMissingPoints] = useState(false);
    const [activeTab, setActiveTab] = useState('available');
    const [sortDir, setSortDir] = useState('descend');
    const [showTransactionDetails, setShowTransactionDetails] = useState(false);
    const [selectedActivityData, setSelectedActivityData] = useState(null);
    const [rewardsFilter, setRewardsFilter] = useState(30);
    const [activityFilter, setActivityFilter] = useState(30);
    const [pendingFilterAnalytics, setPendingFilterAnalytics] = useState(null);
    const [missingFilter, setMissingFilter] = useState(30);
    const [rewardsFilterDates, setRewardsFilterDates] = useState([]);
    const [activityFilterDates, setActivityFilterDates] = useState([]);

    useEffect(() => {
        if (historyDetails?.filterDates?.length > 0) {
            setRewardsFilterDates(historyDetails.filterDates);
        }
    }, [historyDetails?.filterDates]);

    useEffect(() => {
        if (activityHistoryDetails?.filterDates?.length > 0) {
            setActivityFilterDates(activityHistoryDetails.filterDates);
        }
    }, [activityHistoryDetails?.filterDates]);

    /* istanbul ignore next */
    useEffect(() => {
        if (!pendingFilterAnalytics) return;
        const { tab, filterValue, filterText } = pendingFilterAnalytics;
        const isRewards = tab === 'rewards';
        const selectedTabData = isRewards ? historyDetails : activityHistoryDetails;
        if (!selectedTabData?.limit) return;
        const numberOfResults = isRewards
            ? String(historyDetails.numberOfCerts || 0)
            : String(activityHistoryDetails.numberOfActivities || 0);
        const analyticsData = {
            valueName: filterValue,
            sortByType: filterText,
            numberOfResults,
            resultsPageNumber: '1',
            resultsPerPage: String(selectedTabData.limit),
        };
        dispatch(getHistoryFilterData(analyticsData));
        setPendingFilterAnalytics(null);
    }, [pendingFilterAnalytics, historyDetails, activityHistoryDetails]);

    useEffect(() => {
        dispatch(setDefaultTabHistory(deviceType.isMobile));
        dispatch(getRewardsHistory(1, LIMIT, ''));
        dispatch(getRewardsHistoryPL('rewards history'));
        dispatch(getAppliedCashpass());
        return () => dispatch(setDefaultTabHistory('default'));
    }, [dispatch]);

    const handleApplyCashpass = (certificate) => {
        dispatch(applyCashpass(certificate));
    };

    const handleRemoveCashpass = ({ id, serialNumber }) => {
        dispatch(removeCashpass({ id, serialNumber }));
    };

    const handleClearCashpassError = () => {
        dispatch(clearCashpassError());
    };

    const handleTabChange = (value) => {
        if (value !== 'available' && cashpassHasError) {
            dispatch(clearCashpassError());
        }
        setActiveTab(value);
        setSortDir('descend');
        if (value === 'rewards') {
            if (!historyDetails?.rewardsHistory) dispatch(getRewardsHistory(1, LIMIT, ''));
            dispatch(getRewardsHistoryPL('rewards history'));
        } else if (value === 'activity') {
            if (!activityHistoryDetails?.transactions) dispatch(getActivityHistory(1, LIMIT, ''));
            dispatch(getActivityHistoryPL('activity history'));
        } else if (value === 'missing') {
            dispatch(getMissingPointsHistory(1, LIMIT, missingFilter));
            dispatch(missingPointsLoadAction());
        }
    };

    const handleSortClick = () => {
        const next = sortDir === 'descend' ? 'ascend' : 'descend';
        if (activeTab === 'rewards' && historyDetails?.rewardsHistory?.length > 0) {
            setSortDir(next);
            dispatch(getRewardsHistory(historyDetails?.page || 1, LIMIT, rewardsFilter, next));
        } else if (activeTab === 'activity' && activityHistoryDetails?.transactions?.length > 0) {
            setSortDir(next);
            dispatch(
                getActivityHistory(activityHistoryDetails?.page || 1, LIMIT, activityFilter, next)
            );
        }
    };

    const handlePageChange = (page) => {
        if (activeTab === 'rewards') {
            dispatch(getRewardsHistory(page, LIMIT, rewardsFilter, sortDir));
        } else if (activeTab === 'activity') {
            dispatch(getActivityHistory(page, LIMIT, activityFilter, sortDir));
        } else if (activeTab === 'missing') {
            dispatch(getMissingPointsHistory(page, LIMIT, missingFilter));
        }
    };

    /* ── Shared empty state (inside card) ── */
    const renderEmptyState = (
        msg = 'There is currently no history available for the selected date range.'
    ) => (
        <div className={dt(['py-4', 'flex', 'flex-col', 'gap-4'])}>
            <p
                className={dt([
                    'text-[20px]',
                    'font-bold',
                    'leading-8',
                    'text-center',
                    'text-black',
                ])}
            >
                {msg}
            </p>
            <p className={dt(['text-base', 'text-center', 'text-black'])}>
                Please try another option.
            </p>
        </div>
    );

    /* ── Tab 1: Available Rewards ── */
    const renderAvailableRewards = () => (
        <>
            {availableCertificates.length > 0 ? (
                <AvailableRewards
                    certificates={availableCertificates}
                    appliedMap={appliedCashpassMap}
                    onApply={handleApplyCashpass}
                    onRemove={handleRemoveCashpass}
                    enableExclusionsSlidePanel={
                        featureFlags?.enableCashpassExclusionsSlidePanel !== false
                    }
                />
            ) : (
                /* Figma: NO card wrapper for Available empty — just the empty text directly */
                <div
                    className={dt(['py-4', 'flex', 'flex-col', 'gap-4', 'w-full'])}
                    data-automation-id="at-available-rewards-empty"
                >
                    <p
                        className={dt([
                            'text-[20px]',
                            'font-bold',
                            'leading-8',
                            'text-center',
                            'text-black',
                        ])}
                    >
                        There are currently no rewards available to view.
                    </p>
                    <p className={dt(['text-base', 'text-center', 'text-black'])}>
                        Please try another option.
                    </p>
                </div>
            )}
        </>
    );

    /* ── Tab 2: Rewards History ── */
    const renderRewardsHistory = () => {
        const hasData = historyDetails?.rewardsHistory?.length > 0;
        return (
            <HistoryCard
                title="Rewards History"
                filterDates={rewardsFilterDates}
                selectedFilter={rewardsFilter}
                onFilterChange={(e) => {
                    const filter = Number(e.target.value);
                    const filterText = e.target.options?.[e.target.selectedIndex]?.innerText || '';
                    setRewardsFilter(filter);
                    setPendingFilterAnalytics({ tab: 'rewards', filterValue: filter, filterText });
                    dispatch(getRewardsHistory(1, LIMIT, filter));
                    dispatch(getRewardsHistoryPL('rewards history'));
                }}
                currentPage={historyDetails?.page || 1}
                totalPages={historyDetails?.pages || 0}
                onPageChange={handlePageChange}
                isDesktop={deviceType.isDesktop}
            >
                {hasData ? (
                    <NewHistoryData
                        data={historyDetails.rewardsHistory || []}
                        messagesTexts={messagesTexts}
                        data-automation-id="at-rewards-history-tab"
                    />
                ) : (
                    renderEmptyState()
                )}
            </HistoryCard>
        );
    };

    /* ── Tab 3: Activity History ── */
    const renderActivityHistory = () => {
        const hasData = activityHistoryDetails?.transactions?.length > 0;
        return (
            <>
                <HistoryCard
                    title="Activity History"
                    filterDates={activityFilterDates.length > 0 ? activityFilterDates : rewardsFilterDates}
                    selectedFilter={activityFilter}
                    onFilterChange={(e) => {
                        const filter = Number(e.target.value);
                        const filterText = e.target.options?.[e.target.selectedIndex]?.innerText || '';
                        setActivityFilter(filter);
                        setPendingFilterAnalytics({ tab: 'activity', filterValue: filter, filterText });
                        dispatch(getActivityHistory(1, LIMIT, filter));
                        dispatch(getActivityHistoryPL('activity history'));
                    }}
                    currentPage={activityHistoryDetails?.page || 1}
                    totalPages={activityHistoryDetails?.pages || 0}
                    onPageChange={handlePageChange}
                    isDesktop={deviceType.isDesktop}
                >
                    {hasData ? (
                        <div
                            className={dt([
                                'w-full',
                                'border-t',
                                'border-solid',
                                'border-[#D8D8D8]',
                            ])}
                            data-automation-id="at-activity-history-tab"
                        >
                            <NewActivityHistoryData
                                data={activityHistoryDetails.transactions || []}
                                onGetDetails={(data) => {
                                    setSelectedActivityData(data);
                                    setShowTransactionDetails(true);
                                    dispatch(
                                        transactionDetailsModal(
                                            'View rewards transaction details - modal'
                                        )
                                    );
                                }}
                            />
                        </div>
                    ) : (
                        renderEmptyState()
                    )}
                </HistoryCard>
                {showTransactionDetails && (
                    <ActivityHistoryDetails
                        getTransactionDetailsAction={(id) => dispatch(getTransactionDetails(id))}
                        showTransactionDetails={showTransactionDetails}
                        onCloseTransactionsDetails={() => {
                            setShowTransactionDetails(false);
                            setSelectedActivityData(null);
                        }}
                        transactionDetails={transactionDetails}
                        messagesTexts={messagesTexts}
                        preferences={preferences}
                        activityData={selectedActivityData}
                        patchHeader={() => {}}
                        isNative={isNative}
                        featureFlags={featureFlags}
                    />
                )}
            </>
        );
    };

    /* ── Tab 4: Missing Points History ── */
    const renderMissingPoints = () => {
        if (!featureFlags?.isMissingTabEnabled) return null;
        const hasData = missingPointsHistory?.data?.length > 0;
        return (
            <HistoryCard
                title="Missing Points History"
                filterDates={missingPointsHistory?.filterDates || rewardsFilterDates}
                selectedFilter={missingFilter}
                onFilterChange={(e) => {
                    const filter = Number(e.target.value);
                    setMissingFilter(filter);
                    dispatch(getMissingPointsHistory(1, LIMIT, filter));
                }}
                topContent={<MissingPointsBanner showMissing={handleAddMissingPoints} />}
                currentPage={missingPointsHistory?.page || 1}
                totalPages={missingPointsHistory?.pages || 0}
                onPageChange={handlePageChange}
                isDesktop={deviceType.isDesktop}
                data-automation-id="at-missing-points-tab"
            >
                {hasData ? (
                    <NewMissingPointsDetails
                        missingPointsHistory={missingPointsHistory}
                        messagesTexts={messagesTexts}
                    />
                ) : (
                    renderEmptyState()
                )}
            </HistoryCard>
        );
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'available':
                return renderAvailableRewards();
            case 'rewards':
                return renderRewardsHistory();
            case 'activity':
                return renderActivityHistory();
            case 'missing':
                return renderMissingPoints();
            default:
                return null;
        }
    };

    return (
        /* Figma: outer section — bg-gray-05, py-10, gap-4, items-center, relative */
        <div
            id="historyPanel"
            className={dt([
                'relative',
                'w-full',
                'flex',
                'flex-col',
                'bg-light-white',
                'px-6',
                'py-8',
                'gap-4',
                'md:py-10',
                'md:gap-6',
                'md:items-center',
                'lg:px-0',
                'lg:py-10',
                'lg:gap-4',
            ])}
            data-automation-id="at-rewards-history"
        >
            {(showLoader || cashpassLoading) && (
                <Loader keepOverlay automationId="rewards-history-loader" />
            )}

            {/* Decorative background pattern — full width repeat */}
            <div
                className={dt([
                    'absolute',
                    'pointer-events-none',
                    'top-0',
                    'left-0',
                    'right-0',
                    'bottom-0',
                    'bg-repeat',
                    'z-0',
                ])}
                style={{
                    backgroundImage: `url(${RewardsBgPattern})`,
                    backgroundSize: 'auto 640px',
                }}
                aria-hidden="true"
            />

            {/* Title row: SM=col(dropdown below), MD=row(dropdown right), LG=row(title only) */}
            <div
                className={dt([
                    'relative',
                    'flex',
                    'flex-col',
                    'w-full',
                    'gap-4',
                    'z-[1]',
                    'md:flex-row',
                    'md:justify-between',
                    'md:items-center',
                    'md:gap-0',
                    'lg:px-6',
                ])}
            >
                <span className={dt(['font-semibold', 'text-[20px]', 'leading-8', 'text-black'])}>
                    Rewards
                </span>
                {/* SM+MD dropdown — hidden at LG via .tabNav */}
                <ResponsiveTabNav
                    tabs={TABS}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    ariaLabel="Rewards history categories"
                    className={dt(['relative', 'w-full', 'z-[1]', 'md:w-auto', 'lg:hidden'])}
                    tabClassName={dt(['!text-lg'])}
                />
            </div>

            {/* Cashpass error/info message — between title row and tab nav per Figma */}
            {cashpassHasError && (
                <div className={dt(['relative', 'w-full', 'z-[1]', 'lg:px-6'])} data-automation-id="at-cashpass-error">
                    <MessageBox
                        showMessage
                        showIcon
                        hasClose
                        type={
                            cashpassErrorCode === EMPTY_CART_RESTRICT_REWARDS
                                ? 'information'
                                : 'error'
                        }
                        level="section"
                        title={cashpassError || CASHPASS_ERROR_FALLBACK}
                        onClose={handleClearCashpassError}
                    />
                </div>
            )}

            {/* LG/XL tabs — separate row below titleRow */}
            <ResponsiveTabNav
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                ariaLabel="Rewards history categories"
                className={dt(['hidden', 'lg:block', 'relative', 'w-full', 'px-6', 'z-[1]'])}
                showTabs
                tabClassName={dt(['!text-lg'])}
            />

            {/* Tab content — constrained to 776px wide */}
            <div className={dt(['relative', 'w-full', 'z-[1]', 'lg:px-6'])}>
                {renderTabContent()}
            </div>

            {showMissingPoints && (
                <MissingPointsModal
                    showModal={showMissingPoints}
                    closeModal={() => setShowMissingPoints(false)}
                />
            )}
        </div>
    );
};

export default RewardsHistory;
